<template>
  <div class="space-y-8 p-8">
    <!-- Dynamic Competitor Filter Buttons -->
    <div class="flex flex-wrap gap-4">
      <button
        v-for="filter in competitorFilters"
        :key="filter.name"
        :data-filter="filter.name"
        :class="[
          'filter-btn',
          { active: activeFilters.includes(filter.name) },
          'bg-gray-200 px-4 py-2 rounded-full flex items-center space-x-2 border'
        ]"
        @click="toggleFilter(filter.name)"
      >
        <span
          :class="[
            'w-6 h-6 rounded-full flex items-center justify-center text-xs',
            filter.badgeClass
          ]"
        >
          <span :class="filter.textClass">{{ filter.label }}</span>
        </span>
        <span>{{ filter.displayName }}</span>
      </button>
    </div>

    <!-- Content Blocks -->
    <ContentBlock
      v-for="(block, index) in filteredContentBlocks"
      :key="index"
      :block="block"
    />
    <div v-if="isLoading" class="loading-animation">
      Loading...
    </div>
  </div>
</template>

<script>
import ContentBlock from "./ContentBlock.vue";
import { useMainStore } from "../stores/useMainStore";
import { onMounted, onUnmounted, ref, computed } from "vue";

export default {
  components: {
    ContentBlock,
  },
  setup() {
    const mainStore = useMainStore();

    // Load more data on scroll
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 100;
      if (scrollPosition >= threshold) {
        mainStore.loadMoreData();
      }
    };

    onMounted(() => {
      mainStore.fetchData();
      window.addEventListener("scroll", handleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    // Build competitor filters dynamically.
    // Use the lower-case, no-space version of the company name (like in ContentBlock.vue) so that filtering works.
    const competitorFilters = computed(() => {
      const baseFilters = [
        {
          name: "all",
          label: "All",
          displayName: "All",
          badgeClass: "bg-black",
          textClass: "text-white text-xs",
        }
      ];
      if (mainStore.sidebarItems && mainStore.sidebarItems.length) {
        const dynamicFilters = mainStore.sidebarItems.map((item) => ({
          name: item.name ? item.name.toLowerCase().replace(/\s/g, "") : "unknown",
          label: item.abbreviation || (item.name ? item.name.slice(0, 2).toUpperCase() : "??"),
          displayName: item.name || "Unknown",
          badgeClass: item.backgroundColor || "bg-gray-400",
          textClass: item.textColor || "text-gray-900 text-xs",
        }));
        return baseFilters.concat(dynamicFilters);
      }
      return baseFilters;
    });

    // Active filters state – start with "all" selected.
    const activeFilters = ref(["all"]);

    // Toggle filter logic
    function toggleFilter(filterName) {
      if (filterName === "all") {
        activeFilters.value = ["all"];
      } else {
        if (activeFilters.value.includes(filterName)) {
          activeFilters.value = activeFilters.value.filter((f) => f !== filterName);
        } else {
          // Remove "all" if adding a specific competitor filter.
          activeFilters.value = activeFilters.value.filter((f) => f !== "all");
          activeFilters.value.push(filterName);
        }
        // If no filter remains active, default back to "all"
        if (activeFilters.value.length === 0) {
          activeFilters.value = ["all"];
        }
      }
    }

    // Filter content blocks based on active competitor filters.
    const filteredContentBlocks = computed(() => {
      if (activeFilters.value.includes("all")) {
        return mainStore.contentBlocks;
      }
      return mainStore.contentBlocks.filter((block) => activeFilters.value.includes(block.category));
    });

    return {
      competitorFilters,
      activeFilters,
      toggleFilter,
      filteredContentBlocks,
      isLoading: mainStore.isLoading,
    };
  },
};
</script>

<style>
.loading-animation {
  text-align: center;
  padding: 20px;
}

.filter-btn {
  transition: all 0.3s ease;
}

/* Active button styling */
.filter-btn.active {
  background-color: #B203BC; /* Purple background for active buttons */
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>
